// apps/web/src/layouts/components/SidebarApprovalsBadge.tsx
//
// Badge dinâmica de pendências (Pricing > Aprovações). Conta — igual à fila
// /admin/pricing/approvals e ao pill do PricingAdminShell — Change Sets em
// approval_pending cross-pilar MAIS os entries-direct em status='approval_pending'
// que não geram CS: pricing_adjustment (Pilar 3) + deductible + acceptance_rule
// (Pilar 4). CS e entry-direct são disjuntos (CS ⇒ entity published; entry ⇒
// entity approval_pending sem CS), então a soma não duplica.
//
// O conteúdo vive num componente interno renderizado só quando o usuário tem
// permissão — assim as 4 queries nem montam para quem não pode aprovar/publicar.

import { useQueryClient } from '@tanstack/react-query';

import {
  pricingAdjustmentsKeys,
  pricingAdminKeys,
  pricingChangeSetKeys,
  useAcceptanceRulesAdmin,
  useAdjustments,
  useChangeSetsList,
  useDeductiblesCount,
  usePricingPermissions,
  useSocketEvent,
} from '@alamo/api';

export interface SidebarApprovalsBadgeProps {
  variant?: 'highlighted' | 'muted';
  ariaLabel?: string;
}

export function SidebarApprovalsBadge({ variant, ariaLabel }: SidebarApprovalsBadgeProps) {
  const { canApprove, canPublish } = usePricingPermissions();
  if (!(canApprove || canPublish)) return null;
  return <SidebarApprovalsBadgeInner variant={variant} ariaLabel={ariaLabel} />;
}

function SidebarApprovalsBadgeInner({
  variant = 'highlighted',
  ariaLabel,
}: SidebarApprovalsBadgeProps) {
  const queryClient = useQueryClient();

  // Realtime: o backend emite `pricing.approvals.count_changed` (room admin
  // auto-join) a cada transição de change-set de/para approval_pending. Invalida
  // as contagens que compõem o badge — a de change-sets é a que o evento dirige;
  // as 3 entry-direct (Pilar 3 + 4) entram para o total reconvergir. Os keys de
  // deductibles/acceptance carregam objeto de filtro, então invalidamos o prefixo
  // do domínio. useSocketEvent adquire o socket compartilhado, então a própria
  // assinatura mantém a conexão viva enquanto o badge está montado.
  useSocketEvent('pricing.approvals.count_changed', () => {
    void queryClient.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    void queryClient.invalidateQueries({ queryKey: pricingAdjustmentsKeys.all });
    void queryClient.invalidateQueries({ queryKey: [...pricingAdminKeys.all, 'deductibles'] });
    void queryClient.invalidateQueries({ queryKey: [...pricingAdminKeys.all, 'acceptance-rules'] });
  });

  const csQuery = useChangeSetsList(
    { status: ['approval_pending'], limit: 1 },
    { refetchInterval: 300_000, refetchOnWindowFocus: true, refetchOnReconnect: true },
  );
  const entryAdjustmentsQuery = useAdjustments({ pageSize: 1, status: 'approval_pending' });
  const entryDeductiblesQuery = useDeductiblesCount({ status: 'approval_pending' });
  const entryAcceptanceQuery = useAcceptanceRulesAdmin({ limit: 1, status: 'approval_pending' });

  const total =
    (csQuery.data?.count ?? 0) +
    (entryAdjustmentsQuery.data?.meta.total ?? 0) +
    (entryDeductiblesQuery.data ?? 0) +
    (entryAcceptanceQuery.data?.total ?? 0);

  if (total === 0) return null;

  const style =
    variant === 'highlighted' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-800';

  return (
    <span
      data-testid="sidebar-approvals-badge"
      aria-label={ariaLabel ?? `${total} pendências de pricing`}
      className={
        'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 ' +
        'text-[10px] font-body font-semibold tabular-nums ' +
        style
      }
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}
